import React from 'react';
import MainLayout from '../../layouts/MainLayout';
import { useCallSessionStore, SESSION_STATUS } from '../../stores/callSessionStore';
import {
  startSession,
  stopSession,
  changeAvailability,
  submitOutcome,
  hangupCall,
  crearTareaDeSeguimiento,
  cancelarTareaProgramada,
} from '../../components/call-session/callSessionController';
import MediaWarning from '../../components/call-session/MediaWarning';
import ScreeningNotice from '../../components/call-session/ScreeningNotice';
import SessionBar from './components/SessionBar';
import DebtorBriefing from './components/DebtorBriefing';
import CompanyProfilePanel from './components/CompanyProfilePanel';
import OutcomeButtons from './components/OutcomeButtons';
import AccionesTrasTipificar from './components/AccionesTrasTipificar';
import ColaYBuscador from './components/ColaYBuscador';
import ManagementPanel from '../ManagementListPage/components/ManagementPanel/ManagementPanel';
import ExpedientDetailsPanel from '../ManagementListPage/components/ExpedientDetailsPanel';
import AssignmentsSection from '../../components/AssignmentsSection/AssignmentsSection';
import useExpedienteDeLaLlamada from './hooks/useExpedienteDeLaLlamada';

/**
 * La jornada del gestor, en una pantalla.
 *
 * El diseño entero sale de una cuenta: 390 minutos útiles y ~117 s por
 * conversación. Todo lo que le obligue a mirar otro sitio o a escribir sale de
 * ahí. Por eso el gestor **no elige a quién llamar, no marca, no cuelga un
 * no-contesta y no descarta un buzón**: pulsa "Activar sesión" una vez y a partir
 * de ahí solo tipifica.
 *
 * ## Esta pantalla ya NO es dueña de la sesión
 *
 * Antes lo era, y por eso salir de ella cortaba el audio y sacaba al gestor del
 * marcador. Ahora la sesión vive en `components/call-session`, por encima del
 * router: aquí solo se pinta. **Navegar no para nada**; solo la paran el botón de
 * pausa, el de cerrar sesión y cerrar la pestaña.
 *
 * Mientras el gestor está en otra pantalla, la barra flotante
 * (`CallSessionDock`) le enseña quién está al teléfono y le deja tipificar.
 *
 * ## El audio se abre UNA vez
 *
 * `TelnyxRTC` se conecta al activar sesión y no se desconecta hasta cerrarla. No
 * se crea una conexión por llamada a propósito: así no hay "conectando…", ni
 * pitido de handshake, ni medio segundo de retraso. El deudor dice "¿Sí?" y el
 * gestor ya lo está oyendo.
 */
const CallSessionPage = () => {
  const status = useCallSessionStore((s) => s.status);
  const current = useCallSessionStore((s) => s.current);
  const pendingOutcome = useCallSessionStore((s) => s.pendingOutcome);
  const accionesTrasTipificar = useCallSessionStore((s) => s.accionesTrasTipificar);
  const cerrarAcciones = useCallSessionStore((s) => s.cerrarAcciones);
  const queueDepth = useCallSessionStore((s) => s.queueDepth);
  const stats = useCallSessionStore((s) => s.stats);
  const mediaWarning = useCallSessionStore((s) => s.mediaWarning);
  const screening = useCallSessionStore((s) => s.screening);

  const ringing = status === SESSION_STATUS.DIALING;

  // El expediente entero del deudor al que se está llamando. Se pide en cuanto
  // entra la marcación: los ~22 s de tono son el briefing.
  const { expediente, cargando: cargandoExpediente } = useExpedienteDeLaLlamada(current?.expedientId);

  return (
    <MainLayout>
      {/*
        Más ancho que antes: la pantalla ya no es solo la llamada, lleva el
        expediente entero debajo en dos columnas.
      */}
      <div className='mx-auto max-w-[1600px] space-y-4 p-4'>
        <SessionBar
          status={status}
          queueDepth={queueDepth}
          stats={stats}
          busy={status === SESSION_STATUS.CONNECTING}
          onStart={startSession}
          onStop={stopSession}
          onPause={() => changeAvailability(SESSION_STATUS.PAUSED)}
          onResume={() => changeAvailability(SESSION_STATUS.READY)}
          onHangup={hangupCall}
        />

        {/*
          Va ARRIBA del todo y en rojo a propósito: si el gestor no se entera de
          que no le oyen, se pasa la jornada hablando solo — y esas llamadas
          entran en los KPIs como conversaciones útiles si duran más de 45 s.
        */}
        <MediaWarning warning={mediaWarning} />

        {/*
          Un filtro de llamadas suena igual que un buzon. Si el gestor no lo
          sabe, cuelga: pierde a alguien localizable y ademas se programa un
          reintento que se encontrara el mismo filtro.
        */}
        <ScreeningNotice screening={screening} />

        {/*
          Debajo de los avisos y plegado por defecto: no compite con el briefing,
          que es lo que hay que leer durante los ~22 s de tono. Se abre cuando el
          gestor quiere mirar qué viene o adelantar a alguien.
        */}
        {status !== SESSION_STATUS.OFFLINE && <ColaYBuscador status={status} />}

        {status === SESSION_STATUS.OFFLINE ? (
          <div className='rounded-lg border border-dashed border-gray-200 p-12 text-center'>
            <p className='text-gray-500'>
              Activa la sesión y las llamadas empezarán a entrarte solas.
            </p>
            <p className='mt-2 text-sm text-gray-400'>
              Puedes seguir trabajando en otras pantallas: la sesión no se corta al navegar.
            </p>
          </div>
        ) : (
          <>
            {/*
              Las dos mitades del briefing, lado a lado: a quién llamas y con qué
              autoridad. El perfil de la empresa se lee en los MISMOS segundos de
              tono que el resto; más abajo, en la pantalla de un portátil, queda
              fuera de vista justo cuando descuelgan.

              El ancho de la derecha es el de la columna del expediente de abajo,
              para que no se rompa la vertical al hacer scroll.
            */}
            <div className='flex flex-col gap-4 lg:flex-row'>
              <div className='min-w-0 flex-1'>
                <DebtorBriefing target={current} ringing={ringing} />
              </div>
              {current?.company && (
                <div className='w-full flex-shrink-0 lg:w-[420px]'>
                  <CompanyProfilePanel company={current.company} />
                </div>
              )}
            </div>

            {/*
              La tipificación de la ANTERIOR se pinta mientras suena la
              siguiente. Es lo que recupera los ~20 s de wrap: el gestor cierra
              una en el hueco de los tonos de la otra.
            */}
            {pendingOutcome && (
              <div className='rounded-lg border border-gray-200 bg-white p-5'>
                <OutcomeButtons onSelect={submitOutcome} pendingFor={pendingOutcome.debtorName} />
              </div>
            )}

            {/*
              Y lo que se hace DESPUÉS de tipificar, separado a propósito: la
              tipificación dice lo que pasó, esto decide lo que viene. Convive
              con la tipificación de la siguiente sin estorbarla.
            */}
            {accionesTrasTipificar && (
              <AccionesTrasTipificar
                acciones={accionesTrasTipificar}
                onCrearTarea={crearTareaDeSeguimiento}
                onCancelarProgramada={cancelarTareaProgramada}
                onCerrar={cerrarAcciones}
              />
            )}

            {/*
              EL EXPEDIENTE ENTERO, debajo de la llamada.
              ============================================
              Son LOS MISMOS componentes de la pantalla de expedientes, no una
              copia: `ManagementPanel` -que ya trae dentro el resumen de IA- y
              `ExpedientDetailsPanel` reciben un `expedient`, y
              `AssignmentsSection` un id. Reconstruirlos aquí habría significado
              dos sitios donde arreglar cada cosa, y uno de los dos se quedaría
              atrás.

              Se carga EN CUANTO ENTRA LA MARCACIÓN, no al descolgar: los ~22 s
              de tono son el briefing del gestor. Si llegara con la voz del
              deudor, llegaría tarde.

              Y va DEBAJO a propósito: lo que se mira cuando suena el teléfono es
              quién es y cuánto debe —eso está arriba, a todo lo ancho—. El
              expediente es para la conversación, no para el tono.
            */}
            {expediente && (
              <div className='flex flex-col gap-4 lg:flex-row'>
                <div className='min-w-0 flex-1'>
                  <ManagementPanel expedient={expediente} />
                </div>
                <div className='flex w-full flex-shrink-0 flex-col gap-4 lg:w-[420px]'>
                  <AssignmentsSection expedientId={current?.expedientId} fullHeight={false} />
                  <ExpedientDetailsPanel expedient={expediente} />
                </div>
              </div>
            )}

            {cargandoExpediente && !expediente && (
              <p className='text-center text-sm text-gray-400'>Cargando el expediente…</p>
            )}
          </>
        )}

        {/*
          El elemento de audio NO está aquí: vive en `CallSessionHost`, por
          encima del router. Si lo pintara esta pantalla, navegar durante una
          conversación dejaría el audio sin dónde sonar.
        */}
      </div>
    </MainLayout>
  );
};

export default CallSessionPage;
